import { Button } from '@outdoor-voices/ui';
import Image from 'next/image';
import jwtDecode from 'jwt-decode';
import Link from 'next/link';
import React, { useEffect, useState } from 'react';

import { getAccessToken } from '../../libs/accessToken';
import { useLoyaltyStatusQuery, useMeWithProfileQuery } from '../../generated/graphql';
import css from './hero.module.scss';
import loyaltyOptedIn from '../../utils/loyaltyOptedIn';
import ProgressReport from './ProgressReport';

type Token = {
  firstName: string;
};

const LoggedOut: React.FC = () => (
  <Button big white ariaLabel="Click to sign up" href="/users/register" Link={Link}>
    Sign Up
  </Button>
);

const LoggedIn: React.FC = () => {
  const token = getAccessToken();
  const firstName = (jwtDecode(token) as Token).firstName;
  const { data, loading } = useLoyaltyStatusQuery();
  const { data: dataMe, loading: loadingMe } = useMeWithProfileQuery();

  if (loadingMe) {
    return <></>;
  }

  const { profile } = dataMe.me;
  const { preferences } = profile;
  const optedIn = loyaltyOptedIn(preferences);

  if (!optedIn) {
    return (
      <>
        <p className={css.copy}>
          You are currently opted out of OV Rewards. <br />
          Opt-in to score points and unlock perks.
        </p>
        <Button big white ariaLabel="Click to sign up" href="/settings#rewards" Link={Link}>
          Opt-in
        </Button>
      </>
    );
  }

  const points =
    !loading && data ? `, you have ${data.loyaltyStatus.pointsBalance.toLocaleString('en-US')} points` : '';
  return (
    <>
      <p className={css.copy}>
        Hey, Hi, Hello, {firstName}. Welcome back{points}.
      </p>
      <Button big white ariaLabel="Click to view your reward status" Link={Link} href="/users/rewards">
        View Your Reward Status
      </Button>
      <div className={css.progressReport}>
        <ProgressReport white />
      </div>
    </>
  );
};

const Hero: React.FC = () => {
  const [mounted, setMounted] = useState(false);
  const loggedIn = getAccessToken() !== undefined;

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <div className={css.root}>
      <div aria-hidden="true" className={css.desktop} role="presentation">
        <Image
          alt="People running through a field"
          src="/loyalty/hero.jpg"
          layout="responsive"
          height="800"
          width="1420"
          priority
        />
      </div>
      <div aria-hidden="true" className={css.mobile} role="presentation">
        <Image
          alt="People running through a field"
          src="/loyalty/hero-mobile.jpg"
          layout="responsive"
          height="500"
          width="376"
          priority
        />
      </div>
      <div className={css.text}>
        <h1 className={css.title}>
          <Image
            alt="OV Rewards"
            height="244"
            layout="responsive"
            objectFit="contain"
            src="/loyalty/ov-rewards.png"
            width="806"
            priority
          />
        </h1>
        <div className={css.subtitle}>
          <div className={css.desktop}>
            <Image
              alt="Make Moves, Score Points"
              height="176"
              layout="responsive"
              objectFit="contain"
              src="/loyalty/subtitle.png"
              width="1629"
              priority
            />
          </div>
          <div className={css.mobile}>
            <Image
              alt="Make Moves, Score Points"
              height="310"
              layout="responsive"
              objectFit="contain"
              src="/loyalty/subtitle-mobile.png"
              width="799"
              priority
            />
          </div>
        </div>
        {mounted && (
          <>
            {!loggedIn && <LoggedOut />}
            {loggedIn && <LoggedIn />}
          </>
        )}
      </div>
    </div>
  );
};
export default Hero;
